import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

const useTableHook = ({
  columnKeyPrefix,
  queryKeyPrefix,
  fetchFn,
  fetchColumnFn,
  defaultPageRequest = {
    pageNumber: 1,
    pageSize: 0,
    sortColumn: 'createdAt',
    sortDirection: 'desc',
    filters: [],
  },
  tableName,
  defaultExternalFilters = {},
  onPageLoadTrigger = true,
  tableQueryOptions = {},
}) => {
  const [pageRequest, setPageRequest] = useState(defaultPageRequest);
  const [externalFilters, setExternalFilters] = useState(
    defaultExternalFilters,
  );
  const [columnName, setColumnName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({});
  const [activeRows, setActiveRows] = useState([]);
  const [pageLoadTrigger, setPageLoadTrigger] = useState(onPageLoadTrigger);
  const queryKey = [queryKeyPrefix, pageRequest];

  // Table Listing API Call
  const {
    data,
    isLoading: isLoadingData,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => fetchFn(pageRequest),
    keepPreviousData: true,
    onSuccess: () => {
      setActiveRows([]);
    },
    enabled: pageLoadTrigger,
    ...tableQueryOptions,
  });

  const tableDataMutation = useMutation({
    mutationFn: customPageRequest => fetchFn(customPageRequest),
  });

  // Column API Call
  const {
    data: columnData,
    isLoading: isLoadingColumn,
    error: columnError,
    refetch: columnRefetch,
  } = useQuery({
    queryKey: [columnKeyPrefix, columnName, pageRequest],
    queryFn: () =>
      fetchColumnFn({
        columnName,
        tableName,
        ...pageRequest,
      }),
    enabled: !!columnName,
  });

  // Handle Sort
  const handleSort = selectedColumn => {
    const isSameColumn = pageRequest.sortColumn === selectedColumn;
    const columnSortDirection =
      isSameColumn && pageRequest.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    // Update the page request with the new sort column and direction
    setPageRequest(prev => ({
      ...prev,
      sortColumn: selectedColumn,
      sortDirection: columnSortDirection,
    }));
  };

  // Apply Filter
  const applyFilter = colFilters => {
    // Set Up Selected Filter Object with key as column name and value as selected filters
    const selectedFilterObject = {
      key: columnName,
      values: colFilters,
    };
    // Check if the selected filter object key already exists in the filters array
    const existingFilterIndex = pageRequest.filters.findIndex(
      colFilter => colFilter.key === columnName,
    );
    let updatedFilters;
    // If it exists, update the values
    if (existingFilterIndex !== -1) {
      updatedFilters = [...pageRequest.filters];
    } else {
      // If it doesn't exist, add the new filter object to the filters array
      updatedFilters = [...pageRequest.filters, selectedFilterObject];
      setPageRequest(prev => ({
        ...prev,
        filters: updatedFilters,
      }));
    }
    setColumnName('');
  };

  // Reset Active Rows
  useEffect(() => {
    setActiveRows([]);
  }, [pageRequest]);

  // Handle Row Check
  const handleRowCheck = (id, checked) => {
    if (checked) {
      setActiveRows(prev => [...prev, id]);
    } else {
      setActiveRows(prev => prev.filter(row => row !== id));
    }
  };

  // Select All Rows or remove All Rows
  const selectAllRows = (isChecked, isIndex = true, idKey = null) => {
    const dataArray = data?.data || [];
    if (isChecked) {
      if (!isIndex) {
        if (dataArray?.length > 0) {
          const ids = [];
          dataArray.forEach(item => {
            if (idKey) {
              ids.push(item?.[idKey || 'id']);
            }
          });
          setActiveRows(ids);
        }
      } else if (dataArray?.length > 0) {
        const ids = [];
        dataArray.forEach((item, index) => {
          ids.push(index);
        });
        setActiveRows(ids);
      }
    } else {
      setActiveRows([]);
    }
  };

  // Reset all active rows
  const resetActiveRows = () => {
    setActiveRows([]);
  };

  // Reset Filters
  const resetFilters = () => {
    setPageRequest(prev => ({
      ...prev,
      filters: [],
    }));
  };

  // Set Next Page
  const nextPage = () => {
    if (!data?.hasNextPage) return;
    setPageRequest(prev => ({
      ...prev,
      pageNumber: prev.pageNumber + 1,
    }));
  };

  // Set Previous Page
  const prevPage = () => {
    if (!data?.hasPreviousPage) return;
    setPageRequest(prev => ({
      ...prev,
      pageNumber: prev.pageNumber - 1,
    }));
  };

  // Set handle set external filters
  const handleSetExternalFilters = (key, value) => {
    setExternalFilters(prevFilters => ({
      ...prevFilters,
      [key]: value || '',
    }));
  };

  // Apply External Filters
  const applyExternalFilters = (extFilter = null) => {
    if (extFilter) {
      setPageRequest({ ...pageRequest, ...extFilter });
    } else {
      setPageRequest({ ...pageRequest, ...externalFilters });
    }
  };

  // Handle Manual sort
  const handleManualSort = (selectedColumnName, sortDirection) => {
    setPageRequest(prev => ({
      ...prev,
      sortColumn: selectedColumnName,
      sortDirection,
    }));
  };

  // Clear single Filter if exists
  const clearSingleFilter = colName => {
    const updatedFilters = pageRequest.filters.filter(
      singleFilter => singleFilter.key !== colName,
    );
    setPageRequest(prev => ({
      ...prev,
      filters: updatedFilters,
    }));
  };

  // Clear single filter value from a existing filter
  const clearSingleFilterValue = (colName, value) => {
    setPageRequest(prev => ({
      ...prev,
      filters: prev.filters.reduce((acc, singleFilter) => {
        if (singleFilter.key !== colName) {
          acc.push(singleFilter);
        } else {
          const newVals = singleFilter.values.filter(
            singleValue => singleValue !== value,
          );
          if (newVals.length) {
            acc.push({ ...singleFilter, values: newVals });
          }
        }
        return acc;
      }, []),
    }));
  };

  // Check if the column is sorted. If yes, return "ASC" or "DESC" else ""
  const checkColumnIfSorted = colName => {
    if (pageRequest.sortColumn === colName) {
      return pageRequest.sortDirection === 'ASC' ? 'ASC' : 'DESC';
    }
    return '';
  };

  // Get Page Size
  const getPageSize = () => pageRequest.pageSize || 0;

  const setPageSize = size => {
    setPageRequest(prev => ({
      ...prev,
      pageSize: size,
      pageNumber: 1,
    }));
    setActiveRows([]);
  };

  const handleAutoSort = key => {
    const currentSort = checkColumnIfSorted(key);
    const newSortDirection = currentSort === 'ASC' ? 'DESC' : 'ASC';
    handleManualSort(key, newSortDirection);
  };

  return {
    data: data || [],
    columnData: columnData || [],
    totalCount: data?.totalCount || 0,
    isLoadingData,
    isLoadingColumn,
    isError,
    error,
    columnError,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    tableName,
    refetch,
    columnRefetch,
    handleSort,
    columnName,
    setColumnName,
    searchTerm,
    setSearchTerm,
    applyFilter,
    filter,
    setFilter,
    activeRows,
    handleRowCheck,
    selectAllRows,
    resetFilters,
    nextPage,
    prevPage,
    handleSetExternalFilters,
    applyExternalFilters,
    resetActiveRows,
    setPageLoadTrigger,
    tableDataMutation,
    handleManualSort,
    clearSingleFilter,
    clearSingleFilterValue,
    checkColumnIfSorted,
    getPageSize,
    setPageSize,
    handleAutoSort,
  };
};

export default useTableHook;
